(ns app.front.ui.tasks-panel
  "Global side panel that lists user's tasks grouped by workspace (expandable)."
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.business.model :as business-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.review.model :as review-model]
            [app.front.dashlets :as dashlets]
            [app.front.task :as task]
            [app.front.ui.collapsible :as ui-c]
            [app.front.ui.panels :as panels]
            [app.front.ui.select :as ui-select]
            [app.front.ui.side-panel :as side-panel]
            [app.front.util :as futil]
            [app.front.view.party.common :as party-common]
            [app.front.view.workspace.subs :as ws-subs]
            [app.front.view.workspace.util :as ws-util]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::panel-open?
  :<- [::panels/open? :navigation/tasks]
  :-> boolean)

(rf/reg-sub ::task-items
  :<- [::task/sorted-task-items task/default-task-dashlet-id]
  (fn [raw-items _]
    ;; Only keep true tasks to avoid passing acknowledgements into task UI
    (filter (eu/is-type-fn :entity.type/task) raw-items)))

(rf/reg-sub ::ref-ids
  :<- [::task-items]
  (fn [task-items _]
    (->> task-items
         (keep :task/reference)
         set)))

(rf/reg-sub ::refs
  :<- [::ref-ids]
  (fn [ref-ids _]
    (<sub [::dashlets/dashlet-entities task/default-task-dashlet-id ref-ids])))

(rf/reg-sub ::ref-by-id
  :<- [::refs]
  (fn [refs _]
    (into {} (keep (fn [e] (when-let [id (:db/id e)] [id e]))) refs)))

(rf/reg-sub ::items-with-ref
  :<- [::ref-by-id]
  :<- [::task-items]
  (fn [[ref-by-id task-items] _]
    (mapv (fn [t]
            {:task t
             :ref  (get ref-by-id (:task/reference t))})
          task-items)))

(rf/reg-sub ::has-items?
  :<- [::task-items]
  :-> (comp boolean seq))

(rf/reg-sub ::workspace-summary
  :<- [::task/workspace-summary task/default-task-dashlet-id]
  :-> identity)

(rf/reg-sub ::task-count
  :<- [::task-items]
  :-> count)

(rf/reg-event-fx ::toggle-open
  (fn [{:keys [db]} _]
    (let [was-open? (get-in db [:panels/open? :navigation/tasks])]
      (merge
        {:fx [[:dispatch [::panels/toggle :navigation/tasks]]]}
        (when was-open?
          {:db (-> db
                   (dissoc ::expanded-groups)
                   (dissoc ::expanded-tasks))})))))

(defn- some-str [coll]
  (some (fn [v] (let [s (when (some? v) (str v))]
                  (when (and s (not (str/blank? s))) s)))
        coll))

(defn- go-to-ws-link [tr ws-id ws-summary]
  (when ws-id
    (let [workspace   (assoc ws-summary :workspace/id ws-id)
          description (if (ws-util/resume-application? workspace)
                        :search/resume-application
                        :tasks/open-workspace)]
      [:a.inline-flex.items-center.gap-1.no-underline.font-semibold.uppercase.text-sm
       {;; Stringify id only when building href
        :href (ws-util/workspace-or-resume-application-href
                workspace
                (href/href :view/workspace {:workspace-id (str ws-id)}))}
       (tr [description]) " →"])))

(def ^:private actions-compact
  "[&_.bx--btn]:!h-7 [&_.bx--btn]:!min-h-7 [&_.bx--btn]:!py-1 [&_.bx--btn]:!px-3 [&_.bx--btn]:text-xs [&_.bx--btn]:leading-4 ")

(def ^:private title-span-sm
  :span.truncate.font-bold.leading-4.text-ui-night-sky-100.text-sm)

(def ^:private count-badge-span-xs
  :span.inline-block.uppercase.tracking-wide.rounded.px-2.py-1.font-semibold.leading-4.text-ui-night-sky-100.text-xs)

(def ^:private timestamp-span-sm
  :span.leading-4.shrink-0.text-ui-night-sky-100.text-sm)


(def ^:private subline-div
  :div.mt-1.min-w-0.text-ui-muted.text-sm.leading-4)

(def ^:private ws-info-name-div
  :div.mt-1.font-bold.leading-5.text-ui-night-sky-100.text-base)

(defn- workspace-node
  "Return the node from which workspace fields can be read (closest common parent)."
  [task ref]
  (let [task-type (:task/type task)
        by-type   (cond
                    (= :task.type/invite task-type) (-> ref :invite/application :entity/parent)
                    (= :task.type/comment task-type) (-> ref :entity/parent :entity/parent :entity/parent)
                    (= :task.type/attachment-requirements task-type) (-> ref :entity/parent)
                    (= :task.type/party-role-requirements task-type) (-> ref :entity/parent :entity/parent)
                    (review-model/review-task-type? task-type) (-> ref :entity/parent :entity/parent)
                    (= :task.type/application task-type) (-> ref :entity/parent)
                    (= :task.type/permit-sign-off task-type) (-> ref :entity/parent :entity/parent)
                    (= :task.type/due-payment task-type) (-> ref :entity/parent :entity/parent)
                    (= :task.type/approval task-type) (-> ref :entity/parent))]
    (or by-type
        (-> ref :application :entity/parent)
        (-> ref :entity/parent)
        ref
        {})))

(defn- w-get [node k]
  (some-str [(get node k)
             (get-in node [:entity/parent k])
             (get-in node [:entity/parent :entity/parent k])]))

(defn- w-id [node]
  (or (:workspace/id node)
      (get-in node [:entity/parent :workspace/id])
      (get-in node [:entity/parent :entity/parent :workspace/id])))

(defn- resolve-workspace
  [task ref]
  (let [n       (workspace-node task ref)
        ;; Return raw workspace id without stringifying to preserve type for comparisons
        id      (w-id n)
        address (some-str [(w-get n :workspace/address)])
        name    (some-str [(w-get n :workspace/name)
                           (w-get n :workspace/title)])]
    {:id      id
     :address address
     :title   name}))

(defn- bucket-by-date [date-fn created]
  (let [one-day-ms (* 24 60 60 1000)
        created-s  (date-fn created)
        today      (js/Date.)
        yest       (js/Date. (- (.getTime today) one-day-ms))
        today-s    (date-fn today)
        yest-s     (date-fn yest)]
    (cond
      (= created-s today-s) :today
      (= created-s yest-s) :yesterday
      :else :older)))

(rf/reg-sub ::expanded-groups :-> ::expanded-groups)

(rf/reg-sub ::expanded-tasks :-> ::expanded-tasks)

(def ^:private default-group-sort-order :tasks-sort/newest-to-oldest)

(rf/reg-sub ::group-sort-order
  (fn [db _]
    (get db ::group-sort-order default-group-sort-order)))

(rf/reg-event-db ::set-group-sort-order
  (fn [db [_ sort-order]]
    (assoc db ::group-sort-order sort-order)))

(rf/reg-sub ::panel-view-model
  :<- [::date-util/date-fn]
  :<- [::group-sort-order]
  :<- [::items-with-ref]
  :<- [::ws-subs/workspace]
  (fn [[date-fn sort-order items-with-ref current-ws] _]
    (let [current-ws-id              (:workspace/id current-ws)
          in-workspace?              (some? current-ws-id)
          {verification-items true
           workspace-items    false} (group-by #(= :task.type/property-verification
                                                   (-> % :task :task/type))
                                               items-with-ref)
          rows                       (map (fn [{:keys [task ref] :as row}]
                                            (let [{:keys [id title address]} (resolve-workspace task ref)
                                                  ws-name                    (or address title (some-> id str))
                                                  created                    (:entity/created task)]
                                              (assoc row
                                                     :ws-id id
                                                     :ws-name ws-name
                                                     :created created
                                                     :in-this? (and in-workspace? (= current-ws-id id)))))
                                          workspace-items)
          grouped                    (group-by :in-this? rows)
          in-this                    (get grouped true)
          not-in-this                (get grouped false)
          this-items                 (->> in-this
                                          (sort-by :created #(compare %2 %1))
                                          (mapv #(assoc % :kind :task)))
          other-rollups              (->> not-in-this
                                          (group-by :ws-id)
                                          (map (fn [[ws-id ws-rows]]
                                                 (let [tasks        (->> ws-rows
                                                                         (sort-by :created #(compare %2 %1))
                                                                         (mapv #(assoc % :kind :task)))
                                                       sort-created (:created (first tasks))]
                                                   {:kind         :ws-rollup
                                                    :ws-id        ws-id
                                                    :ws-name      (or (:ws-name (first ws-rows))
                                                                      (some-> ws-id str))
                                                    :bucket       (bucket-by-date date-fn sort-created)
                                                    :sort-created sort-created
                                                    :tasks        tasks})))
                                          (sort-by :sort-created (if (= sort-order :tasks-sort/newest-to-oldest)
                                                                   #(compare %2 %1)
                                                                   compare))
                                          vec)]
      {:in-workspace?            in-workspace?
       :this-workspace-task-rows this-items
       :other-workspace-rollups  other-rollups
       :other-task-rows          verification-items})))

(rf/reg-event-db ::toggle-group
  (fn [db [_ group-id single-tid]]
    (let [expanded-groups (or (::expanded-groups db) #{})
          group-open?     (contains? expanded-groups group-id)
          toggled-groups  (if group-open?
                            (disj expanded-groups group-id)
                            (conj expanded-groups group-id))
          task-op         (if group-open? disj conj)
          with-groups     #(assoc % ::expanded-groups toggled-groups)]
      (if single-tid
        (-> db
            with-groups
            (update ::expanded-tasks (fnil task-op #{}) single-tid))
        (with-groups db)))))

(rf/reg-event-db ::toggle-task
  (fn [db [_ tid]]
    (update db ::expanded-tasks (fnil util/toggle-in-set #{}) tid)))

(defn- task-title [tr lang task ref]
  (let [tt (:task/type task)]
    (cond
      (nil? tt) ""
      (= tt :task.type/application)
      (or (-> ref :entity/parent (config-model/category-name lang))
          (-> ref :entity/parent :workspace/permit-type vector tr))
      :else
      (tr [tt]))))

(defn- task-subline [tr lang task ref]
  (let [date-fn (<sub [::date-util/date-fn])
        tt      (:task/type task)]
    (or
      (when (and tt (:task/due task))
        (str (tr [:task/due]) " " (date-fn (:task/due task))))
      (cond
        (= :task.type/invite tt)
        (let [roles (->> ref :invite/party :party/party-roles
                         (map party-common/party-role-label)
                         (str/join ", "))]
          (str (tr [:invite/invitation-leading-text]) " " roles))
        (review-model/review-task-type? tt) (review-model/review-name ref lang)
        (= :task.type/due-payment tt) (tr [(:bill/phase ref)])
        (= :task.type/acceptance tt) (str (-> ref :acceptance/value vector tr) ": "
                                          (-> ref :entity/parent :entity/type vector tr))
        (= :task.type/application tt) (or (-> ref :entity/parent :workspace/municipal-case-id)
                                          (-> ref :entity/parent :workspace/id))
        (= :task.type/join-business tt) (business-model/format-name ref lang)
        (= :task.type/approval tt) (tr [:task/approval-required])
        (= :task.type/bond-verification tt) (str (tr [:bond/applicant]) ": "
                                                 (bond-model/bond-owner-name lang (:bond/owner ref)))
        (= :task.type/property-verification tt)
        (tr [:property/verification-task-count] [(:task/description task)])
        :else (or (-> ref :entity/parent :workspace/address)
                  (-> ref :workspace/address))))))

(defn- task-row
  [{:keys [task ref inline? tr lang]} expanded?]
  (let [tid          (:db/id task)
        {:keys [id]} (resolve-workspace task ref)
        date-time-fn (<sub [::date-util/date-time-fn])
        created      (date-time-fn (:entity/created task))
        header-id    (str "task-header-" tid)
        body-id      (str "task-body-" tid)
        status       :default
        title-text   (task-title tr lang task ref)
        subline-text (task-subline tr lang task ref)]
    [ui-c/collapsible
     {:open?     expanded?
      :on-toggle #(>evt [::toggle-task tid])
      :variant   :filled ;; filled card
      :status    status ;; orange left
      :header
      [:div.px-4.py-2.min-w-0.w-full.pr-6
       [:div.font-bold.truncate.text-ui-night-sky-100.text-base.leading-5
        title-text]

       [:div.mt-0.5.flex.items-center.justify-between.gap-2.min-w-0>div.text-sm.leading-8.text-ui-night-sky-100.shrink-0
        created]

       ;; Subline row (muted); truncate when collapsed, wrap when expanded
       [subline-div
        {:class (when-not expanded? "truncate")
         :style (when expanded?
                  {:whiteSpace "normal" :wordBreak "break-word"})}
        subline-text]]
      :header-id header-id
      :body-id   body-id}

     ;; Body: action buttons only when expanded
     (when expanded?
       [:div.px-4.py-3
        (if inline?
          ;; Inline variant used inside rollups (vertical spacing)
          [:div.space-y-3 {:class actions-compact}
           (when (:task/type task)
             (if (= (:task/type task) :task.type/invite)
               [task/invite-actions {:tr tr :my-id task/default-task-dashlet-id} task ref]
               [task/task-actions
                {:tr tr :my-id task/default-task-dashlet-id :show-button-labels? true}
                task
                ref
                id]))]
          ;; Top-level task rows (wrap like before)
          [:div.flex.flex-wrap.gap-2 {:class actions-compact}
           (when (:task/type task)
             (if (= (:task/type task) :task.type/invite)
               [task/invite-actions {:tr tr :my-id task/default-task-dashlet-id} task ref]
               [task/task-actions
                {:tr tr :my-id task/default-task-dashlet-id :show-button-labels? true}
                task
                ref
                id]))])])]))

(defn- workspace-info [tr {:keys [ws-name ws-summary]}]
  (let [{ws-id          :workspace/id
         ws-address     :workspace/address
         ws-permit-type :workspace/permit-type
         project-name   :project/name}         ws-summary
        work-type                              (when (keyword? ws-permit-type)
                                                 (tr [ws-permit-type]))]
    [:div.px-4.py-3
     [:div.font-semibold.leading-4.uppercase.tracking-wide.text-ui-muted.text-sm
      project-name]
     [ws-info-name-div
      (or ws-name ws-address ws-id)]
     (when work-type
       [:div.mt-1.leading-4.text-ui-muted.text-sm
        work-type])]))


(defn- rollup-row-view
  [{:keys [ws-id ws-name tasks bucket]} ws-summary expanded-tasks expanded? tr lang]
  (let [first-row    (first tasks)
        first-task   (:task first-row)
        first-ref    (:ref first-row)
        date-time-fn (<sub [::date-util/date-time-fn])
        date-fn      (<sub [::date-util/date-fn])
        time-fn      (<sub [::date-util/time-fn])
        raw-dt       (date-time-fn (:entity/created first-task))
        date         (date-fn (:entity/created first-task))
        time         (time-fn (:entity/created first-task))
        ;; For collapsed groups:
        ;; - today  -> show only time
        ;; - other  -> show only date
        collapsed-ts (cond
                       (= bucket :today) (or time raw-dt)
                       :else (or date raw-dt))
        n            (count tasks)
        single?      (= n 1)
        single-tid   (when single? (:db/id first-task))
        header-id    (str "rollup-header-" ws-id)
        body-id      (str "rollup-body-" ws-id)
        ws-summary*  (get ws-summary ws-id)]
    [ui-c/collapsible
     {:open?     expanded?
      :on-toggle #(>evt [::toggle-group [:ws-rollup ws-id] single-tid])
      :variant   (if expanded? :border :filled)
      :status    (when-not expanded? :default)
      :header-id header-id
      :body-id   body-id
      :header
      (if (not expanded?)
        [:div.min-w-0.w-full.flex-1
         [:div.pl-4.pr-10.py-2>div.flex.items-center.justify-between.gap-2.min-w-0
          [:div.flex.items-center.gap-2.min-w-0
           (if single?
             [title-span-sm
              (task-title tr lang first-task first-ref)]
             [count-badge-span-xs
              (str n " " (tr [:tasks/tasks]))])]
          [timestamp-span-sm collapsed-ts]]

         [:div
          {:style {:width "100%"}}
          [ui-c/divider]]

         [:div.px-4.py-2
          [:div.truncate.font-bold.leading-4.text-ui-night-sky-100.text-sm
           ws-name]
          (let [worktype (some-> ws-summary*
                                 :workspace/permit-type
                                 vector
                                 tr)]
            [:div.mt-1.leading-4.text-ui-muted.text-sm
             worktype])]]

        [:div.pl-4.pr-8.py-2.flex.items-center.justify-between>div.flex.flex-col.min-w-0
         (if single?
           [:span.font-bold.leading-4.text-ui-night-sky-100.text-sm
            (task-title tr lang first-task first-ref)]
           [count-badge-span-xs
            (str n " " (tr [:tasks/tasks]))])
         [:span.mt-1.leading-4.shrink-0.text-ui-muted.text-sm
          collapsed-ts]])}

     [workspace-info tr {:ws-name    ws-name
                         :ws-summary ws-summary*}]

     [:div.px-2.py-2
      (for [{:keys [task ref]} tasks
            :let               [k (:db/id task)]]
        ^{:key (str "t-" k)}
        [task-row {:task task :ref ref :inline? true :tr tr :lang lang}
         (contains? expanded-tasks k)])]

     [ui-c/divider]
     [:div.px-4.py-3.flex.items-center.justify-center
      (go-to-ws-link tr ws-id ws-summary*)]]))

(defn- sort-selector [{:keys [tr disabled?]}]
  [:div.w-full
   [ui-select/select {:id           "tasks-panel-group-sort"
                      :class-name   "full-width-select"
                      :allow-empty? false
                      :disabled     (boolean disabled?)
                      :label-text   (tr [:tasks/sort])
                      :hide-label   true
                      :values       [:tasks-sort/newest-to-oldest
                                     :tasks-sort/oldest-to-newest]
                      :val->str     (fn [v]
                                      (case v
                                        :tasks-sort/newest-to-oldest (tr [:tasks/sort-newest-to-oldest])
                                        :tasks-sort/oldest-to-newest (tr [:tasks/sort-oldest-to-newest])
                                        (name v)))
                      :on-change    #(>evt [::set-group-sort-order %])
                      :value        (<sub [::group-sort-order])
                      :tr           tr}]])

(def rollup-container :div.w-full.space-y-3.min-w-0)

(defn- task-rows-view [{:keys [task-rows tr lang]}]
  (let [expanded-tasks (<sub [::expanded-tasks])]
    [rollup-container
     (futil/for-frag [{:keys [task] :as row} task-rows]
       ^{:key (:db/id task)}
       [task-row (merge (select-keys row [:task :ref])
                        {:tr tr :lang lang})
        (contains? expanded-tasks (:db/id task))])]))

(defn- rollup-rows-view [{:keys [rollups tr lang]}]
  (let [ws-summary      (<sub [::workspace-summary])
        expanded-groups (<sub [::expanded-groups])
        expanded-tasks  (<sub [::expanded-tasks])]
    [rollup-container
     (futil/for-frag [row rollups]
       (let [roll-id        [:ws-rollup (:ws-id row)]
             roll-expanded? (contains? expanded-groups roll-id)]
         ^{:key (str "roll-" (pr-str roll-id))}
         [rollup-row-view row ws-summary expanded-tasks roll-expanded? tr lang]))]))

(defn- empty-this-workspace-view [tr]
  [:div.w-full.bg-ui-light-gray.rounded.px-4.py-2.text-center.text-ui-night-sky-100.text-sm
   (tr [:tasks/no-pending])])

(defn tasks-panel [tr]
  (let [{:keys [in-workspace?
                this-workspace-task-rows
                other-workspace-rollups
                other-task-rows]}        (<sub [::panel-view-model])

        panel-open?        (<sub [::panel-open?])
        has-other-rollups? (seq other-workspace-rollups)
        has-other-tasks?   (seq other-task-rows)
        lang               (<sub [:language])
        rollups-view       [rollup-rows-view (util/mmap {:rollups other-workspace-rollups} tr lang)]
        other-task-view    (when has-other-tasks?
                             [task-rows-view (util/mmap {:task-rows other-task-rows} tr lang)])]
    [side-panel/side-panel {:panel-open? panel-open? :panel-id :tasks}
     [side-panel/title (if in-workspace?
                         (tr [:tasks/in-this-workspace])
                         (tr [:navigation/tasks]))]
     [:div.flex.flex-col.items-start.gap-4.w-full.box-border
      (cond
        in-workspace?
        [:<>
         (if (seq this-workspace-task-rows)
           [task-rows-view (util/mmap {:task-rows this-workspace-task-rows}
                                      tr lang)]
           [empty-this-workspace-view tr])

         [ui-c/divider]
         [side-panel/title (tr [:tasks/in-other-workspaces])]
         [sort-selector (util/mmap {:disabled? (not has-other-rollups?)}
                                   tr)]
         (when has-other-rollups?
           rollups-view)
         (when has-other-tasks?
           [:<>
            [ui-c/divider]
            [side-panel/title (tr [:tasks/other])]
            other-task-view])]

        (or has-other-rollups? has-other-tasks?)
        [:<>
         (when has-other-rollups?
           [:<>
            [sort-selector (util/mmap {:disabled? false}
                                      tr)]
            rollups-view])
         (when (and has-other-rollups? has-other-tasks?)
           [ui-c/divider])
         other-task-view]

        :else
        [:div.mt-2.text-ui-night-sky-100.opacity-70
         (tr [:tasks/empty])])]]))
